import {isLoggedIn} from '@github-ui/client-env'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {Link} from '@github-ui/react-router'
import {
  getIconColorClassName,
  getIconComponent,
  SAVED_VIEW_OPEN_SOURCES,
  type SavedViewOpenLocationState,
} from '@github-ui/saved-views'
import {LABELS} from '@github-ui/saved-views/constants'
import {splitViewsByVisibility} from '@github-ui/saved-views/utils/views-visibility'
import {PinIcon} from '@primer/octicons-react'
import {NavList} from '@primer/react'
import type {ComponentProps} from 'react'

import styles from './PinnedViewsNav.module.css'

export interface SidebarPinnedView {
  id: number | string
  name: string
  icon: string
  color: string
  visibility: string
}

const PINNED_GROUP_LABEL = 'Pinned'
const PINNED_NAV_LABEL = 'Pinned views'
const SIDEBAR_OPEN_LOCATION_STATE: SavedViewOpenLocationState = {
  savedViewOpenSource: SAVED_VIEW_OPEN_SOURCES.sidebar,
}

export interface PinnedViewsNavProps {
  pinnedViews: SidebarPinnedView[]
  viewsPath: string
  pathname: string
  forceHardNavigation: boolean
  collapsible: boolean
  pinnedViewsTeamVsPersonalEnabled?: boolean
  onSelectView?: (id: number | string) => void
}

export function PinnedViewsNav({
  pinnedViews,
  viewsPath,
  pathname,
  forceHardNavigation,
  collapsible,
  pinnedViewsTeamVsPersonalEnabled = false,
  onSelectView,
}: PinnedViewsNavProps) {
  if (pinnedViews.length === 0) return null

  const renderItem = (view: SidebarPinnedView) => {
    const to = `${viewsPath}/${encodeURIComponent(view.id)}`
    return (
      <PinnedViewItem
        key={view.id}
        view={view}
        to={to}
        aria-current={pathname === to ? 'page' : undefined}
        forceHardNavigation={forceHardNavigation}
        onClick={() => onSelectView?.(view.id)}
      />
    )
  }

  const items = pinnedViews.map(renderItem)

  const renderFlatList = (children: Array<ReturnType<typeof renderItem>>, title: string = PINNED_GROUP_LABEL) => {
    if (collapsible) {
      return (
        <>
          <hr className={styles.dividerCollapsible} />
          <CollapsibleNavList aria-label={PINNED_NAV_LABEL} className={styles.pinnedNav}>
            <CollapsibleNavList.Group icon={PinIcon} title={title}>
              {children}
            </CollapsibleNavList.Group>
          </CollapsibleNavList>
        </>
      )
    }

    return (
      <>
        <hr className={styles.divider} />
        <NavList aria-label={PINNED_NAV_LABEL} className={styles.pinnedNav}>
          <NavList.Group title={title}>{children}</NavList.Group>
        </NavList>
      </>
    )
  }

  if (!pinnedViewsTeamVsPersonalEnabled) {
    return renderFlatList(items)
  }

  const {personal, team} = splitViewsByVisibility(pinnedViews)
  const loggedIn = isLoggedIn()
  const personalItems = loggedIn ? personal.map(renderItem) : []
  const teamItems = team.map(renderItem)

  if (personalItems.length === 0 && teamItems.length === 0) return null

  if (!loggedIn) {
    return renderFlatList(teamItems, LABELS.views.team)
  }

  if (collapsible) {
    const sections = [
      ...(personalItems.length > 0 ? [{key: 'personal', title: LABELS.views.personal, children: personalItems}] : []),
      {key: 'team', title: LABELS.views.team, children: teamItems},
    ]

    return (
      <>
        <hr className={styles.dividerCollapsible} />
        <CollapsibleNavList aria-label={PINNED_NAV_LABEL} className={styles.pinnedNav}>
          <CollapsibleNavList.Group icon={PinIcon} title={PINNED_GROUP_LABEL} sections={sections} />
        </CollapsibleNavList>
      </>
    )
  }

  return (
    <>
      <hr className={styles.divider} />
      <NavList aria-label={PINNED_NAV_LABEL} className={styles.pinnedNav}>
        {personalItems.length > 0 ? (
          <NavList.Group title={LABELS.views.personal} hideDivider>
            {personalItems}
          </NavList.Group>
        ) : null}
        {teamItems.length > 0 ? (
          <NavList.Group title={LABELS.views.team} hideDivider={personalItems.length === 0}>
            {teamItems}
          </NavList.Group>
        ) : null}
      </NavList>
    </>
  )
}

function PinnedViewItem({
  view,
  to,
  'aria-current': ariaCurrent,
  forceHardNavigation,
  onClick,
}: {
  view: SidebarPinnedView
  to: string
  'aria-current'?: 'page'
  forceHardNavigation: boolean
  onClick?: () => void
}) {
  const linkProps = (
    forceHardNavigation ? {href: to} : {as: Link, to, state: SIDEBAR_OPEN_LOCATION_STATE}
  ) as ComponentProps<typeof NavList.Item>

  return (
    <NavList.Item {...linkProps} aria-current={ariaCurrent} onClick={onClick}>
      <NavList.LeadingVisual>
        <PinnedViewIcon icon={getIconComponent(view.icon)} color={view.color} />
      </NavList.LeadingVisual>
      {view.name}
    </NavList.Item>
  )
}

function PinnedViewIcon({icon: Icon, color}: {icon: ReturnType<typeof getIconComponent>; color: string}) {
  return <Icon size={16} className={getIconColorClassName(color)} />
}
